<template>
  <div
    class="product-box product-box-set-item"
    :data-id="productBoxData.id"
    :data-item-id="productBoxData.itemId"
    :data-artnr="productBoxData.trackingData.artNr"
    :data-wban="productBoxData.trackingData.wban"
  >
    <div
      v-if="!isMobile"
      class="product-box-set-item__image-container"
    >
      <component
        :is="productLink ? 'a' : 'div'"
        :href="productLink"
        :aria-label="productBoxData.linked ? productBoxData.description1 : null"
        class="product-box__image-wrapper"
      >
        <div
          class="product-flags product-box-set-item__product-flags"
          v-if="productBoxData.flags"
        >
          <flags
            :flags="productBoxData.flags"
            :limit="4"
            :award-limit="2"
            :white-list="settings.flags.whitelistInSet"
          />
        </div>
        <div class="product-box-set-item__picture">
          <image-element
            class="product-box-set-item__image"
            :src="imgSrc"
            :alt="productBoxData.images[0].alt"
          />
        </div>
      </component>
    </div>
    <div class="product-box-set-item__body">
      <product-box-segment-header
        class="product-box-set-item__segment-header"
        :product-box-data="productBoxData"
        :state="state"
        :number-of-bottles-in-set="productBoxData.numberOfBottlesInSet && productBoxData.numberOfBottlesInSet > 1 ? productBoxData.numberOfBottlesInSet : null"
        :product-link="productLink"
        @wishlistClicked="toggleWishlist"
      />
      <div class="product-box-set-item__main-info">
        <div class="product-box-set-item__quickfacts-wrapper">
          <div
            v-if="isMobile"
            class="product-box-set-item__image-container"
          >
            <div
              class="product-flags product-box-set-item__product-flags"
              v-if="productBoxData.flags"
            >
              <flags
                :flags="productBoxData.flags"
                :limit="4"
                :award-limit="2"
                :white-list="settings.flags.whitelistInSet"
              />
            </div>
            <component
              :is="productBoxData.linked ? 'a' : 'div'"
              :href="productBoxData.linked ? productBoxData.url : null"
              :aria-label="productBoxData.linked ? productBoxData.description1 : null"
              class="product-box__image-wrapper"
            >
              <div class="product-box-set-item__picture">
                <image-element
                  class="product-box-set-item__image"
                  :src="imgSrc"
                  :alt="productBoxData.images[0].alt"
                  :title="productBoxData.images[0].title"
                  :enabled="productBoxData.lazyLoadImages"
                />
              </div>
            </component>
          </div>
          <div v-if="showQuickfactsWithDesc">
            <quickfacts
              v-if="!hideQuickfacts"
              class="product-box-set-item__quickfacts"
              :quickfacts="quickfactList"
              :use-links="false"
            />
            <div
              class="product-box-set-item__longDescription"
              v-if="productBoxData.longDescription"
              v-html="getLongText(productBoxData.longDescription)"
            />
            <div
              class="product-box-set-item__longDescription product-box-set-item__longDescription--empty"
              v-else
            />
          </div>
          <quickfacts
            v-if="!hideQuickfacts"
            class="product-box-set-item__quickfacts"
            :class="[`product-box-set-item__quickfacts--length-${quickfactList.length}`]"
            :quickfacts="quickfactList"
            :use-links="false"
          />
        </div>
        <div
          class="product-box-set-item__longDescription"
          v-if="productBoxData.longDescription && !showQuickfactsWithDesc"
          v-html="getLongText(productBoxData.longDescription)"
        />
        <div
          class="product-box-set-item__longDescription product-box-set-item__longDescription--empty"
          v-else
        />
        <div
          class="product-box-set-item__conversion"
          v-if="productBoxData.price || productBoxData.linked"
        >
          <div
            class="product-box__price-wrapper"
            :class="`price-info--currency-${productBoxData.price.currency}`"
            v-if="productBoxData.price && !productBoxData.purchaseOnRequestArticle"
          >
            <price-info-oldest
              class="product-box__price"
              :value="productBoxData.price"
              :show-star="false"
            />
          </div>
          <a
            v-if="productBoxData.linked"
            :href="productBoxData.url"
            class="product-box-set-item__button button button--text"
            v-html="$t('detail.product.moreDetails')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {ref, computed} from 'vue';
  import ProductBox from '../productBox/productBox.vue';
  import {excerpt} from '../../utilities/js/excerpt/excerpt';
  import Flags from '../flags/flags.vue';
  import Quickfacts from '../quickfacts/quickfacts.vue';
  import ImageElement from '../imageElement/imageElement.vue';
  import {breakpoints} from '../../variables/variables.js';

  export default {
    name: 'ProductBoxSetItem',
    components: {
      Quickfacts,
      ImageElement,
      Flags
    },

    mixins: [ProductBox],

    props: {
      hideQuickfacts: {
        type: Boolean,
        default: false,
      },
      showQuickfactsWithDesc: { // REFACTOR THIS!
        type: Boolean,
        default: false,
      }
    },

    setup(props) {
      const isMobile = ref<boolean>(null);
      function breakpointChangeHandler() {
        isMobile.value = window.innerWidth < breakpoints.tabletLandscape;
      }
      breakpointChangeHandler();

      const quickfactList = computed(() => props.productBoxData.quickfacts);
      const productLink = computed(() => {
        if (props.productBoxData.linked) {
          return props.productBoxData.url;
        }
        return null;
      });

      function getLongText(text: string): string {
        return excerpt(text, 30);
      }

      const imgSrc = computed(() => {
        return props.productBoxData.images.length > 2
          ? [props.productBoxData.images[1].src, props.productBoxData.images[3].src]
          : [props.productBoxData.images[1].src];
      });

      return {
        isMobile,
        quickfactList,
        productLink,
        getLongText,
        imgSrc
      }
    },
  }
</script>
